import { ref, computed } from '@vue/composition-api'
import { kpEndpoint, kpParams, kpRequest } from '@/axios'
import store from '@/store'
import { FETCH_ALL_SCOPES } from '@/store/modules/auth.module'
import { apiToastSuccess, apiToastWarning } from '@/@core/utils/toast'
import analytics from '@/@core/utils/analytics'
import constants, {
  USER_ROLES, USER_ROLES_LABEL, FLAG_CONSTANTS, NOTIFICATION_PREFERENCE_OPTIONS, NOTIFICATION_PREFERENCE_TYPES,
} from '@/constants'
import { constants as c } from '@kingpin-global/kingpin-utils-frontend'

const { ADDITIONAL_USER_STATUS, ADMIN_USER_STATUS } = c

export function useManageTeam() {
  // State
  const teamMatesList = ref([])
  const isLoadingTeammates = ref(false)
  const currentPage = ref(1)
  const perPage = ref(10)

  // Form state
  const firstName = ref('')
  const lastName = ref('')
  const email = ref('')
  const additionalUserRole = ref('')
  const additionalUserId = ref('')
  const reportingTo = ref(null)
  const isSaving = ref(false)

  // Delete state
  const selectedAdditionalUserForDelete = ref({})

  // Notification state
  const selectedTeammateForNotification = ref(null)
  const isLoadingNotification = ref(false)
  const isSavingNotification = ref(false)
  const notificationPreferences = ref({
    order: 'default',
    shipment: 'default',
    collectionPref: 'default',
  })
  const originalNotificationPreferences = ref({
    order: 'default',
    shipment: 'default',
    collectionPref: 'default',
  })

  // Store-derived computeds
  const currentEntityId = computed(() => store.state.auth.currentEntityId)
  const allScopes = computed(() => store.state.auth.allScopes)
  const salesManagers = computed(() => store.state.auth.salesManagers)
  const isBrand = computed(() => store.getters.isBrand)
  const isEntityAdmin = computed(() => store.getters.isEntityAdmin)
  const isSalesRep = computed(() => store.getters.isSalesRep)

  // teamMatesRoles from store may be Object or Array — normalize to Array of role values
  const teamMatesRoles = computed(() => {
    const roles = store.getters.teamMatesRoles
    if (Array.isArray(roles)) return roles
    if (roles && typeof roles === 'object') return Object.values(roles)
    return []
  })

  // Feature flag
  const isNotificationPreferencesEnabled = computed(
    () => isBrand.value && store.state.flags[FLAG_CONSTANTS.NOTIFICATION_PREFERENCES],
  )

  // Pagination
  const totalAdditionalUsers = computed(() => teamMatesList.value.length)
  const hasPagination = computed(() => totalAdditionalUsers.value > 10)
  const paginatedTeammates = computed(() => {
    const start = (currentPage.value - 1) * perPage.value
    return teamMatesList.value.slice(start, start + perPage.value)
  })

  // Table columns
  const tableColumns = computed(() => {
    const columns = [
      { key: 'firstName', label: 'First Name' },
      { key: 'lastName', label: 'Last Name' },
      { key: 'email', label: 'Email' },
      { key: 'role', label: 'Role' },
      { key: 'status', label: 'Status', sortable: true },
    ]
    if (isNotificationPreferencesEnabled.value && isEntityAdmin.value) {
      columns.push({ key: 'notification', label: 'Notification' })
    }
    columns.push({ key: '_id', label: 'Actions' })
    return columns
  })

  // Form validation
  const isAddTeammateSaveDisabled = computed(() => {
    const flag = isSaving.value
    if (additionalUserRole.value === USER_ROLES.SALESREPRESENTATIVE) {
      return flag || !reportingTo.value
    }
    return flag || !additionalUserRole.value
  })

  // Helper methods
  function extractResponseData(response) {
    return response.data.data || response.data
  }

  function extractTeammates(data) {
    return data.teammates || data || []
  }

  function getFirstName(teammate) {
    return teammate.firstName || teammate.userName?.split(' ')[0] || ''
  }

  function getLastName(teammate) {
    return teammate.lastName || teammate.userName?.split(' ').slice(1).join(' ') || ''
  }

  function mapTeammateToTableItem(teammate) {
    return {
      _id: teammate.userAssociationId || teammate._id,
      firstName: getFirstName(teammate),
      lastName: getLastName(teammate),
      email: teammate.email || teammate.userEmail,
      role: teammate.role,
      status: teammate.status || 'active',
    }
  }

  async function fetchTeammatesFromApi() {
    const endpoint = kpParams(
      kpEndpoint.user.getTeammates,
      currentEntityId.value,
    )
    const response = await kpRequest(endpoint)
    const data = extractResponseData(response)
    const teammates = extractTeammates(data)
    return teammates.map(teammate => mapTeammateToTableItem(teammate))
  }

  async function fetchTeammatesFromStore() {
    await store.dispatch(FETCH_ALL_SCOPES)
    return allScopes.value
  }

  async function loadAllScopes() {
    isLoadingTeammates.value = true
    try {
      if (isNotificationPreferencesEnabled.value) {
        teamMatesList.value = await fetchTeammatesFromApi()
        await store.dispatch(FETCH_ALL_SCOPES)
      } else {
        teamMatesList.value = await fetchTeammatesFromStore()
      }
    } catch {
      // error toast handled by axios interceptor
    } finally {
      isLoadingTeammates.value = false
    }
  }

  // CRUD methods
  async function sendInvite(payload) {
    isSaving.value = true
    try {
      const res = await kpRequest({ ...kpEndpoint.profile.additionalUser.add, payload })
      apiToastSuccess(res.data.message)
      analytics.track(constants.TRACKS.ACTIONS.INVITE_ADDITIONAL_USERS_PROFILE)
      await loadAllScopes()
      return true
    } catch {
      return false
    } finally {
      isSaving.value = false
    }
  }

  async function updateUser(payload) {
    isSaving.value = true
    try {
      const res = await kpRequest({ ...kpEndpoint.profile.additionalUser.update, payload })
      apiToastSuccess(res.data.message)
      await loadAllScopes()
      return true
    } catch {
      return false
    } finally {
      isSaving.value = false
    }
  }

  async function deleteUser() {
    if (!selectedAdditionalUserForDelete.value?._id) return false
    try {
      const res = await kpRequest({
        ...kpParams(kpEndpoint.profile.additionalUser.delete, selectedAdditionalUserForDelete.value._id),
      })
      apiToastSuccess(res.data.message)
      selectedAdditionalUserForDelete.value = {}
      await loadAllScopes()
      return true
    } catch {
      return false
    }
  }

  // Form helpers
  function populateFormForEdit(user) {
    firstName.value = user.firstName
    lastName.value = user.lastName
    email.value = user.email
    additionalUserRole.value = user?.role || ''
    additionalUserId.value = user._id
  }

  function clearFormData() {
    firstName.value = ''
    lastName.value = ''
    email.value = ''
    additionalUserId.value = ''
    additionalUserRole.value = ''
    reportingTo.value = null
    isSaving.value = false
  }

  function parseStatusVariant(status) {
    switch (status.toLowerCase()) {
      case ADDITIONAL_USER_STATUS.PENDING:
        return 'warning'
      case ADDITIONAL_USER_STATUS.ACCEPTED:
      case ADMIN_USER_STATUS.ACTIVE:
        return 'success'
      default:
        return 'danger'
    }
  }

  // Notification methods
  async function fetchNotificationPreferences(teammateId) {
    isLoadingNotification.value = true
    try {
      const endpoint = kpParams(
        kpEndpoint.notificationPreferences.getPreferences,
        teammateId,
      )
      const response = await kpRequest(endpoint)
      const data = response.data.data || response.data
      const preferences = data.preferences || {}

      notificationPreferences.value = {
        order: preferences.order || 'default',
        shipment: preferences.shipment || 'default',
        collectionPref: preferences.collectionPref || 'default',
      }
      originalNotificationPreferences.value = { ...notificationPreferences.value }
    } catch (error) {
      notificationPreferences.value = {
        order: 'default',
        shipment: 'default',
        collectionPref: 'default',
      }
      originalNotificationPreferences.value = { ...notificationPreferences.value }
    } finally {
      isLoadingNotification.value = false
    }
  }

  function isSalesRole(role) {
    return role === USER_ROLES.SALESMANAGER || role === USER_ROLES.SALESREPRESENTATIVE
  }

  function hasPreferenceChangedToAll(key) {
    return notificationPreferences.value[key] === 'all'
      && originalNotificationPreferences.value[key] !== 'all'
  }

  function hasAnyPreferenceChangedToAll() {
    return hasPreferenceChangedToAll('order')
      || hasPreferenceChangedToAll('shipment')
      || hasPreferenceChangedToAll('collectionPref')
  }

  function requiresConfirmation(teammate) {
    return isSalesRole(teammate.role) && hasAnyPreferenceChangedToAll()
  }

  async function saveNotificationPreferences() {
    if (!selectedTeammateForNotification.value) return false

    isSavingNotification.value = true
    try {
      const endpoint = kpParams(
        kpEndpoint.notificationPreferences.updatePreferences,
        currentEntityId.value,
      )

      await kpRequest({
        ...endpoint,
        payload: {
          userAssociationId: selectedTeammateForNotification.value._id,
          preferences: notificationPreferences.value,
        },
      })

      apiToastSuccess('Notification preferences updated successfully')
      return true
    } catch (error) {
      apiToastWarning('Failed to update notification preferences')
      return false
    } finally {
      isSavingNotification.value = false
    }
  }

  function clearNotificationData() {
    selectedTeammateForNotification.value = null
    notificationPreferences.value = {
      order: 'default',
      shipment: 'default',
      collectionPref: 'default',
    }
    originalNotificationPreferences.value = {
      order: 'default',
      shipment: 'default',
      collectionPref: 'default',
    }
  }

  function getNotificationPreferenceLabel(value) {
    const option = NOTIFICATION_PREFERENCE_OPTIONS.find(opt => opt.key === value)
    return option?.label || value || 'Default'
  }

  return {
    // State
    teamMatesList,
    isLoadingTeammates,
    currentPage,
    perPage,
    firstName,
    lastName,
    email,
    additionalUserRole,
    additionalUserId,
    reportingTo,
    isSaving,
    selectedAdditionalUserForDelete,
    selectedTeammateForNotification,
    isLoadingNotification,
    isSavingNotification,
    notificationPreferences,
    originalNotificationPreferences,

    // Computeds
    currentEntityId,
    allScopes,
    salesManagers,
    teamMatesRoles,
    isBrand,
    isEntityAdmin,
    isSalesRep,
    isNotificationPreferencesEnabled,
    totalAdditionalUsers,
    hasPagination,
    paginatedTeammates,
    tableColumns,
    isAddTeammateSaveDisabled,

    // Methods
    loadAllScopes,
    sendInvite,
    updateUser,
    deleteUser,
    populateFormForEdit,
    clearFormData,
    parseStatusVariant,
    fetchNotificationPreferences,
    requiresConfirmation,
    saveNotificationPreferences,
    clearNotificationData,
    getNotificationPreferenceLabel,

    // Constants
    USER_ROLES,
    USER_ROLES_LABEL,
    NOTIFICATION_PREFERENCE_OPTIONS,
    NOTIFICATION_PREFERENCE_TYPES,
  }
}
